import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { Task } from 'redux-saga'
import { SagaReturnType, call, cancel, delay, fork, put, select, take, takeLatest } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'

import { AuthUser } from '@/core/classes/Auth'
import consultationModel, {
  Act,
  Biological,
  Consultation,
  ConsultationReason,
  CreateConsultationDto,
  CreateConsultationReasonDto,
  TwilioStunServer,
  UpdateConsultationDto,
  UpdateConsultationReasonDto,
} from '@/core/models/consultation.model'
import documentModel from '@/core/models/document.model'
import { CalendsoEventType } from '@/core/models/staff.model'
import { JoinMeetingRequestParams, SearchRequest, SearchRequestOptions } from '@/core/types/request'

import {
  startBackgroundRequestRpiId as startBackgroundRequestRpiIdAction,
  stopBackgroundRequestRpiId as stopBackgroundRequestRpiIdAction,
} from '@/redux/reducers/app.reducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import {
  createConsultation as createConsultationAction,
  createConsultationSuccess as createConsultationSuccessAction,
  createConsultationFailure as createConsultationFailureAction,
  createConsultationReason as createConsultationReasonAction,
  createConsultationReasonSuccess as createConsultationReasonSuccessAction,
  createConsultationReasonFailure as createConsultationReasonFailureAction,
  deleteConsultationReason as deleteConsultationReasonAction,
  deleteConsultationReasonSuccess as deleteConsultationReasonSuccessAction,
  deleteConsultationReasonFailure as deleteConsultationReasonFailureAction,
  endConsultation as endConsultationAction,
  endConsultationSuccess as endConsultationSuccessAction,
  endConsultationFailure as endConsultationFailureAction,
  getConsultationReasons as getConsultationReasonsAction,
  getConsultationReasonsSuccess as getConsultationReasonsSuccessAction,
  getConsultationReasonsFailure as getConsultationReasonsFailureAction,
  getConsultation as getConsultationAction,
  getConsultationSuccess as getConsultationSuccessAction,
  getConsultationFailure as getConsultationFailureAction,
  getConsultations as getConsultationsAction,
  getConsultationsSuccess as getConsultationsSuccessAction,
  getConsultationsFailure as getConsultationsFailureAction,
  getRpiMeetingId as getRpiMeetingIdAction,
  getRpiMeetingIdSuccess as getRpiMeetingIdSuccessAction,
  getRpiMeetingIdFailure as getRpiMeetingIdFailureAction,
  getStunServer as getStunServerAction,
  setStunServer as setStunServerAction,
  updateConsultation as updateConsultationAction,
  updateConsultationSuccess as updateConsultationSuccessAction,
  updateConsultationFailure as updateConsultationFailureAction,
  updateConsultationReason as updateConsultationReasonAction,
  updateConsultationReasonSuccess as updateConsultationReasonSuccessAction,
  updateConsultationReasonFailure as updateConsultationReasonFailureAction,
  joinMeeting as joinMeetingAction,
  joinMeetingSuccess as joinMeetingSuccessAction,
  joinMeetingFailure as joinMeetingFailureAction,
  sendMesures as sendMesuresAction,
  sendBiological as sendBiologicalAction,
  deleteBiological as deleteBiologicalAction,
  saveMesures as saveMesuresAction,
  saveMesuresSuccess as saveMesuresSuccessaction,
  saveMesuresFailure as saveMesuresFailureAction,
  sendInvite as sendInviteAction,
  sendInviteSuccess as sendInviteSuccessAction,
  sendInviteFailure as sendInviteFailureAction,
  generateReport as generateReportAction,
  generateReportSuccess as generateReportSuccessAction,
  generateReportFailure as generateReportFailureAction,
  receiveRpiIdRequest as receiveRpiIdRequestAction,
  consultationCreatedSocketEvent,
  consultationUpdatedSocketEvent,
  selectRpiMeetingId,
  sendRpiIdSocketEvent,
  requestRpiIdSocketEvent,
  generateFSP as generateFSPAction,
  generateFSPSuccess as generateFSPSuccessAction,
  generateFSPFailure as generateFSPFailureAction,
  searchParticipants as searchParticipantsAction,
  searchParticipantsSuccess as searchParticipantsSuccessAction,
  searchParticipantsFailure as searchParticipantsFailureAction,
} from '@/redux/reducers/consultation.reducer'

import { handleError } from '@/utils/errors'

dayjs.extend(utc)
dayjs.extend(timezone)

function* bookEvent(authUser: AuthUser, consultation: Consultation, eventType: CalendsoEventType) {
  try {
    yield call(consultationModel.bookMeeting, {
      email: authUser.email,
      end: dayjs.utc(consultation.startDate).add(eventType.duree, 'minute').local().format(),
      eventTypeId: eventType.id,
      eventTypeSlug: eventType.slug,
      guests: [],
      language: 'fr',
      name: authUser.userInfo.name.concat(' ', authUser.userInfo.surname),
      notes: 'https://' + window.location.hostname + '/consultations/' + consultation._id + '/live',
      user: consultation.medecin?._id,
      start: dayjs.utc(consultation.startDate).local().format(),
      timeZone: dayjs.tz.guess(),
    })
  } catch (exception) {
    console.warn(exception)
  }
}

function* createConsultation(action: PayloadAction<{ consultation: CreateConsultationDto; eventType?: CalendsoEventType }>) {
  try {
    const consultation: Consultation = yield call(consultationModel.createConsultation, action.payload.consultation)
    yield put(createConsultationSuccessAction(consultation))
    yield put(consultationCreatedSocketEvent(consultation))
    const authUser: AuthUser = yield select(selectAuthUser)
    if (action.payload.eventType) {
      yield bookEvent(authUser, consultation, action.payload.eventType)
    }
  } catch (exception) {
    console.warn(exception)
    yield put(createConsultationFailureAction(handleError(exception)))
  }
}

function* createConsultationReason(action: PayloadAction<CreateConsultationReasonDto>) {
  try {
    const consultationReason: ConsultationReason = yield call(consultationModel.createConsultationReason, action.payload)
    yield put(createConsultationReasonSuccessAction(consultationReason))
  } catch (exception) {
    yield put(createConsultationReasonFailureAction(handleError(exception)))
  }
}

function* deleteConsultationReason(action: PayloadAction<string>) {
  try {
    yield call(consultationModel.deleteConsultationReason, action.payload)
    yield put(deleteConsultationReasonSuccessAction(action.payload))
  } catch (exception) {
    yield put(deleteConsultationReasonFailureAction(handleError(exception)))
  }
}

function* endConsultation(action: PayloadAction<string>) {
  try {
    const consultationId = action.payload
    const consultation: Consultation = yield call(consultationModel.endConsultation, consultationId)
    yield put(endConsultationSuccessAction(consultation))
  } catch (exception) {
    console.warn(exception)
    yield put(endConsultationFailureAction(handleError(exception)))
  }
}

function* getConsultationReasons(action: PayloadAction<SearchRequest | undefined>) {
  try {
    const consultationReasons: ConsultationReason[] = yield call(consultationModel.getConsultationReasons, action.payload)
    yield put(getConsultationReasonsSuccessAction(consultationReasons))
  } catch (exception) {
    yield put(getConsultationReasonsFailureAction(handleError(exception)))
  }
}

function* getConsultation(action: PayloadAction<string>) {
  try {
    const consultationId = action.payload
    const consultation: Consultation = yield call(consultationModel.getConsultation, consultationId)
    yield put(getConsultationSuccessAction(consultation))
  } catch (exception) {
    console.warn(exception)
    yield put(getConsultationFailureAction(handleError(exception)))
  }
}

function* getConsultations(action: PayloadAction<{ query?: Record<string, any>; options?: SearchRequestOptions }>) {
  try {
    const { query, options } = action.payload
    const [consultations, totalCount]: [Consultation[], number] = yield call(consultationModel.getConsultations, query, options)
    yield put(getConsultationsSuccessAction({ items: consultations, totalCount }))
  } catch (exception) {
    console.warn(exception)
    yield put(getConsultationsFailureAction(handleError(exception)))
  }
}



function* getRpiMeetingId() {
  try {
    const rpiMeetingId: string = yield call(consultationModel.getRpiMeetingId)
    yield put(getRpiMeetingIdSuccessAction(rpiMeetingId || null))
  } catch (exception) {
    console.warn(exception)
    yield put(getRpiMeetingIdFailureAction(handleError(exception)))
  }
}

function* getStunServer() {
  try {
    const stunServer: TwilioStunServer = yield call(consultationModel.getStunServer)
    yield put(setStunServerAction(stunServer))
  } catch (exception) {
    console.warn(exception)
  }
}

function* joinMeeting(action: PayloadAction<JoinMeetingRequestParams>) {
  try {
    const { meetingType } = action.payload
    const { attendee, meeting } = yield call(consultationModel.joinMeeting, action.payload)
    yield put(joinMeetingSuccessAction({ attendeeInfo: attendee, meetingInfo: meeting, meetingType }))
  } catch (exception) {
    console.warn(exception)
    yield put(joinMeetingFailureAction(handleError(exception)))
  }
}

function* updateConsultation(action: PayloadAction<{ consultationId: string; changes: UpdateConsultationDto }>) {
  try {
    const { consultationId, changes } = action.payload
    const consultation: Consultation = yield call(consultationModel.updateConsultation, consultationId, changes)
    yield put(updateConsultationSuccessAction(consultation))
    yield put(consultationUpdatedSocketEvent(consultation))
  } catch (exception) {
    console.warn(exception)
    yield put(updateConsultationFailureAction(handleError(exception)))
  }
}

function* updateConsultationReason(action: PayloadAction<{ consultationReasonId: string; changes: UpdateConsultationReasonDto }>) {
  try {
    const { consultationReasonId, changes } = action.payload
    const consultationReason: ConsultationReason = yield call(consultationModel.updateConsultationReason, consultationReasonId, changes)
    yield put(updateConsultationReasonSuccessAction(consultationReason))
  } catch (exception) {
    yield put(updateConsultationReasonFailureAction(handleError(exception)))
  }
}

function* sendMesures(action: PayloadAction<{ consultationId: string; mesures: Consultation['mesures'] }>) {
  try {
    const { consultationId, mesures } = action.payload
    // need this check to not update with value =0 from msv
    if (mesures.some((mesure) => mesure.value === 0 || mesure.value === '0')) {
      throw new Error('No empty value')
    }
    const consultation: Consultation = yield call(consultationModel.sendMesures, consultationId, mesures)
    yield put(updateConsultationSuccessAction(consultation))
    yield put(consultationUpdatedSocketEvent(consultation))
  } catch (exception) {
    console.warn(exception)
    yield put(updateConsultationFailureAction(handleError(exception)))
  }
}

function* sendBiological(action: PayloadAction<{ consultationId: string; biological: Biological }>) {
  try {
    const { consultationId, biological } = action.payload
    // need this check to not update with value =0 from msv
    const consultation: Consultation = yield call(consultationModel.sendBiological, consultationId, biological)
    yield put(updateConsultationSuccessAction(consultation))
    yield put(consultationUpdatedSocketEvent(consultation))
  } catch (exception) {
    console.warn(exception)
    yield put(updateConsultationFailureAction(handleError(exception)))
  }
}

function* deleteBiological(action: PayloadAction<{ consultationId: string; biologicalId: string }>) {
  try {
    const { consultationId, biologicalId } = action.payload
    // need this check to not update with value =0 from msv
    const consultation: Consultation = yield call(consultationModel.deleteBiological, consultationId, biologicalId)
    yield put(updateConsultationSuccessAction(consultation))
    yield put(consultationUpdatedSocketEvent(consultation))
  } catch (exception) {
    console.warn(exception)
    yield put(updateConsultationFailureAction(handleError(exception)))
  }
}

function* saveMesures(action: PayloadAction<{ consultationId: string; csv: string }>) {
  try {
    const { consultationId, csv } = action.payload
    yield call(consultationModel.saveMesures, consultationId, csv)
    yield put(saveMesuresSuccessaction())
  } catch (exception) {
    console.warn(exception)
    yield put(saveMesuresFailureAction(handleError(exception)))
  }
}

function* sendInvite(action: PayloadAction<ReturnType<typeof sendInviteAction>['payload']>) {
  try {
    const { consultationId, participant } = action.payload
    const consultation: Consultation = yield call(consultationModel.sendInvite, consultationId, participant)
    yield put(sendInviteSuccessAction(consultation))
  } catch (exception) {
    yield put(sendInviteFailureAction(handleError(exception)))
  }
}

function* generateReport(action: PayloadAction<{ consultationId: string; reportValues: Record<string, any> }>) {
  try {
    const { consultationId, reportValues } = action.payload
    yield call(documentModel.generateConsultationReport, consultationId, reportValues)
    yield put(generateReportSuccessAction())
  } catch (exception) {
    yield put(generateReportFailureAction(handleError(exception)))
  }
}

function* generateFSP(action: PayloadAction<{ consultationId: string; acts: Act[] }>) {
  try {
    const { consultationId, acts } = action.payload
    const consultation: Consultation = yield call(consultationModel.getFSP, consultationId, acts)
    yield put(getConsultationSuccessAction(consultation))
    yield put(generateFSPSuccessAction())
  } catch (exception) {
    yield put(generateFSPFailureAction(handleError(exception)))
  }
}

function* sendRpiId(action: PayloadAction<string>) {
  const consultationId = action.payload
  const rpiId: string | null = yield select(selectRpiMeetingId)
  if (rpiId) {
    yield put(sendRpiIdSocketEvent({ consultationId, rpiId }))
  }
}

/**
 * @param consultationId string
 * @param timeInterval in seconds
 */
function* bgRequestRpiId(consultationId: string, timeInterval: number) {
  try {
    while (true) {
      yield put(requestRpiIdSocketEvent(consultationId))
      yield delay(timeInterval * 1000)
    }
  } catch (error) {
    console.log(error)
  }
}

function* watchBackgroundRequestRpiId() {
  while (true) {
    // wait for the user start background action
    const action: PayloadAction<string> = yield take(startBackgroundRequestRpiIdAction.type)

    // starts the task in the background
    const bgGetUploadSpeedTask: Task = yield fork(bgRequestRpiId, action.payload, 30)

    // wait for the user stop action
    yield take(stopBackgroundRequestRpiIdAction.type)

    // cancel the background task
    yield cancel(bgGetUploadSpeedTask)
  }
}

function* searchParticipants(action: PayloadAction<ReturnType<typeof searchParticipantsAction>['payload']>) {
  try {
    const { consultationId, participantType, searchTerm } = action.payload
    const results: SagaReturnType<typeof consultationModel.searchParticipants> = yield call(
      consultationModel.searchParticipants,
      searchTerm,
      participantType,
      consultationId
    )
    yield put(searchParticipantsSuccessAction(results))
  } catch (exception) {
    yield put(searchParticipantsFailureAction(handleError(exception)))
  }
}

const sagas = [
  call(watchBackgroundRequestRpiId),
  takeLatest(createConsultationAction.type, createConsultation),
  takeLatest(createConsultationReasonAction.type, createConsultationReason),
  takeLatest(deleteConsultationReasonAction.type, deleteConsultationReason),
  takeLatest(endConsultationAction.type, endConsultation),
  takeLatest(getConsultationReasonsAction.type, getConsultationReasons),
  takeLatest(getConsultationAction.type, getConsultation),
  takeLatest(getConsultationsAction.type, getConsultations),
  takeLatest(getRpiMeetingIdAction.type, getRpiMeetingId),
  takeLatest(getStunServerAction.type, getStunServer),
  takeLatest(joinMeetingAction.type, joinMeeting),
  takeLatest(updateConsultationAction.type, updateConsultation),
  takeLatest(updateConsultationReasonAction.type, updateConsultationReason),
  takeLatest(sendMesuresAction.type, sendMesures),
  takeLatest(saveMesuresAction.type, saveMesures),
  takeLatest(sendBiologicalAction.type, sendBiological),
  takeLatest(deleteBiologicalAction.type, deleteBiological),
  takeLatest(sendInviteAction.type, sendInvite),
  takeLatest(generateReportAction.type, generateReport),
  takeLatest(receiveRpiIdRequestAction.type, sendRpiId),
  takeLatest(generateFSPAction.type, generateFSP),
  takeLatest(searchParticipantsAction.type, searchParticipants),
]

export default sagas
